package vegasful.admin.views

import androidx.compose.runtime.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Theme
import vegasful.admin.api.Weekday
import vegasful.admin.components.*

// ---------------------------------------------------------------------------------------------
// The stored shape
// ---------------------------------------------------------------------------------------------

/**
 * The client-side shape of a thing to do's `hours` attribute.
 *
 * `hours` crosses the admin API as a **JSON string** rather than a nested input type — deliberately,
 * because the shape gains fields when the Phase 2 verification sweep lands and a deep GraphQL input
 * would need an AppSync deploy each time. That means there is no generated class for it, and this
 * mirrors the server's `OpeningHours` by hand. The server is authoritative: it parses this and
 * rejects anything structurally wrong with a list of problems, which [hoursBox] renders.
 *
 * Source of truth to keep in step with:
 * `vegasful-server/ingestion/content-model/src/main/kotlin/com/steamstreet/vegasful/server/model/hours/OpeningHours.kt`
 *
 * Days are held as plain strings rather than the generated [Weekday] enum on purpose: the first
 * records were written into DynamoDB by hand, and an unrecognised day name should leave a box
 * unchecked rather than fail the whole parse and silently blank the editor.
 */
@Serializable
data class HoursDoc(
    val weekly: List<WeeklyRuleDoc> = emptyList(),
    val overrides: List<DateOverrideDoc> = emptyList(),
    val seasons: List<SeasonDoc> = emptyList(),
    val reviewBy: String? = null,
    val notes: String? = null,
) {
    fun isEmpty(): Boolean =
        weekly.isEmpty() && overrides.isEmpty() && seasons.isEmpty() &&
                reviewBy.isNullOrBlank() && notes.isNullOrBlank()

    companion object {
        /**
         * `ignoreUnknownKeys` keeps a record written by a newer deploy readable here, and
         * `encodeDefaults = false` keeps what we send close to what the server itself writes.
         */
        @OptIn(kotlinx.serialization.ExperimentalSerializationApi::class)
        private val json = Json {
            ignoreUnknownKeys = true
            encodeDefaults = false
            explicitNulls = false
            prettyPrint = true
            prettyPrintIndent = "  "
        }

        /** Parse stored hours, falling back to an empty document rather than throwing. */
        fun parse(value: String?): HoursDoc =
            value?.takeIf { it.isNotBlank() }
                ?.let { runCatching { json.decodeFromString(serializer(), it) }.getOrNull() }
                ?: HoursDoc()

        /** True when there is stored text that could not be read at all. */
        fun isUnreadable(value: String?): Boolean =
            value?.takeIf { it.isNotBlank() }
                ?.let { runCatching { json.decodeFromString(serializer(), it) }.isFailure } == true
    }

    /**
     * Serialize for submission. A range marked closed carries no ranges — the server rejects a
     * date override that is both closed and has ranges, so the two are reconciled here rather than
     * relying on the operator never producing that combination.
     */
    fun encode(): String = json.encodeToString(
        serializer(),
        copy(
            overrides = overrides.map { if (it.closed) it.copy(ranges = emptyList()) else it },
            reviewBy = reviewBy?.ifBlank { null },
            notes = notes?.ifBlank { null },
        )
    )
}

@Serializable
data class WeeklyRuleDoc(
    val days: List<String> = emptyList(),
    val ranges: List<OpenRangeDoc> = emptyList(),
)

@Serializable
data class DateOverrideDoc(
    val date: String = "",
    val closed: Boolean = false,
    val ranges: List<OpenRangeDoc> = emptyList(),
    val note: String? = null,
)

@Serializable
data class SeasonDoc(
    val start: String = "",
    val end: String = "",
    val weekly: List<WeeklyRuleDoc> = emptyList(),
    val name: String? = null,
)

/**
 * A span of being open, stored as an opening time plus a **duration** — never a closing time.
 *
 * The editor never shows the duration. See [minutesOpen] for why the model is shaped this way and
 * what the UI does about it.
 */
@Serializable
data class OpenRangeDoc(
    @SerialName("open") val open: String = "",
    val duration: String = "",
)

// ---------------------------------------------------------------------------------------------
// Clock and duration arithmetic
// ---------------------------------------------------------------------------------------------

private const val MINUTES_PER_DAY = 24 * 60

/** Minutes past midnight for an `HH:MM` clock value. Null when it cannot be read. */
fun parseClock(value: String?): Int? {
    val parts = value?.trim()?.takeIf { it.isNotEmpty() }?.split(":") ?: return null
    if (parts.size < 2) return null
    val hour = parts[0].toIntOrNull() ?: return null
    val minute = parts[1].toIntOrNull() ?: return null
    if (hour !in 0..23 || minute !in 0..59) return null
    return hour * 60 + minute
}

/** `HH:MM` for minutes past midnight, wrapping into a single day. */
fun formatClock(minutes: Int): String {
    val inDay = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return "${(inDay / 60).toString().padStart(2, '0')}:${(inDay % 60).toString().padStart(2, '0')}"
}

/** A human 12-hour rendering, for the read-back hint beside an entered range. */
fun clockLabel(minutes: Int): String {
    val inDay = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    val hour24 = inDay / 60
    val minute = inDay % 60
    val hour12 = when (hour24 % 12) {
        0 -> 12
        else -> hour24 % 12
    }
    val suffix = if (hour24 < 12) "AM" else "PM"
    return if (minute == 0) "$hour12 $suffix"
    else "$hour12:${minute.toString().padStart(2, '0')} $suffix"
}

private val isoDurationPattern =
    Regex("""^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:[.,]\d+)?)S)?$""", RegexOption.IGNORE_CASE)

/**
 * Minutes for an ISO-8601 duration such as `PT14H30M`. Null when unreadable or not positive.
 *
 * Written by hand rather than leaning on [kotlin.time.Duration] so that the exact set of forms the
 * server can produce (`PT16H`, `PT14H30M`, `PT45M`, `PT24H`) round-trips predictably.
 */
fun parseIsoMinutes(value: String?): Int? {
    val match = isoDurationPattern.matchEntire(value?.trim() ?: return null) ?: return null
    val hours = match.groupValues[1].toIntOrNull() ?: 0
    val minutes = match.groupValues[2].toIntOrNull() ?: 0
    val seconds = match.groupValues[3].replace(',', '.').toDoubleOrNull() ?: 0.0
    return (hours * 60 + minutes + (seconds / 60.0).toInt()).takeIf { it > 0 }
}

/** ISO-8601 for a whole number of minutes: 870 becomes `PT14H30M`, 1440 becomes `PT24H`. */
fun formatIsoMinutes(minutes: Int): String {
    val hours = minutes / 60
    val remainder = minutes % 60
    return buildString {
        append("PT")
        if (hours > 0) append("${hours}H")
        if (remainder > 0) append("${remainder}M")
        if (hours == 0 && remainder == 0) append("0M")
    }
}

/**
 * How long a place is open, given the time it opens and the time it closes.
 *
 * This is the conversion the whole editor exists for. The stored model is open + duration and never
 * a closing time, because Las Vegas closes past midnight constantly and a stored `close: 02:00`
 * cannot be told apart from "closed at 2am the same morning". An operator, though, thinks in open
 * and close — so the wrap happens here, once, instead of in the operator's head.
 *
 * A close at or before the open means the next day: 11:30 to 02:00 is 14h30m. Open equal to close
 * reads as a full 24 hours rather than zero, which is how "open 24 hours" gets entered. The result
 * is therefore always between 1 minute and 24 hours, which is exactly the range the server accepts
 * — this conversion cannot produce a duration the server will reject.
 */
fun minutesOpen(openMinutes: Int, closeMinutes: Int): Int {
    val difference = closeMinutes - openMinutes
    return if (difference <= 0) difference + MINUTES_PER_DAY else difference
}

/** The clock time at which a range opening at [openMinutes] for [durationMinutes] closes. */
fun closingMinutes(openMinutes: Int, durationMinutes: Int): Int =
    (openMinutes + durationMinutes) % MINUTES_PER_DAY

private const val ALL_DAY_MINUTES = MINUTES_PER_DAY

// ---------------------------------------------------------------------------------------------
// Immutable list helpers
// ---------------------------------------------------------------------------------------------

private fun <T> List<T>.replacedAt(index: Int, value: T): List<T> =
    toMutableList().also { it[index] = value }

private fun <T> List<T>.withoutAt(index: Int): List<T> =
    toMutableList().also { it.removeAt(index) }

// ---------------------------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------------------------

private val weekdayOrder = listOf(
    Weekday.MONDAY, Weekday.TUESDAY, Weekday.WEDNESDAY, Weekday.THURSDAY,
    Weekday.FRIDAY, Weekday.SATURDAY, Weekday.SUNDAY,
)

private fun Weekday.shortLabel(): String =
    name.take(3).let { it.take(1) + it.drop(1).lowercase() }

/**
 * The opening-hours editor.
 *
 * [stored] is the JSON currently on the record; [save] submits replacement JSON and is expected to
 * throw when the server rejects it, so that the structural problems it reports land next to the
 * fields that caused them rather than only in a toast.
 */
@Composable
fun hoursBox(stored: String?, save: suspend (String) -> Unit) {
    // Keyed on the stored value so that a successful save (which reloads the record) resets the
    // editor to what is actually persisted, rather than leaving stale edits on screen.
    var document by remember(stored) { mutableStateOf(HoursDoc.parse(stored)) }
    var problems by remember(stored) { mutableStateOf<List<String>>(emptyList()) }
    var showJson by remember(stored) { mutableStateOf(false) }

    val dirty = document != HoursDoc.parse(stored)

    box({
        title = "Opening Hours"

        action {
            title = "Save Hours"
            primary = true
            showProgressOnAction = true
            enabled = dirty
            action {
                problems = emptyList()
                val encoded = if (document.isEmpty()) "" else document.encode()
                try {
                    save(encoded)
                } catch (t: Throwable) {
                    problems = hoursProblems(t.message)
                }
            }
        }

        action {
            title = "Revert"
            enabled = dirty
            action {
                document = HoursDoc.parse(stored)
                problems = emptyList()
            }
        }
    }) {
        if (HoursDoc.isUnreadable(stored)) {
            problemList(
                listOf("The stored hours could not be parsed, so this editor started empty. Saving replaces them."),
                warning = true,
            )
        }

        if (problems.isNotEmpty()) {
            problemList(problems)
        }

        // ---- the normal week -------------------------------------------------------------
        sectionHeading("The normal week")
        if (document.weekly.isEmpty()) {
            hint("No weekly hours. That is legitimate for a seasonal attraction that publishes a calendar of open dates instead — put those under Date Overrides.")
        }
        weeklyRulesEditor(document.weekly) { document = document.copy(weekly = it) }

        // ---- date overrides --------------------------------------------------------------
        sectionHeading("Date Overrides")
        hint("Holiday closures, extended New Year's Eve hours. An override always beats a season, and a season beats the normal week.")
        document.overrides.forEachIndexed { index, override ->
            overrideEditor(
                override,
                onChange = { document = document.copy(overrides = document.overrides.replacedAt(index, it)) },
                onRemove = { document = document.copy(overrides = document.overrides.withoutAt(index)) },
            )
        }
        addRowButton("Add a date override") {
            document = document.copy(overrides = document.overrides + DateOverrideDoc())
        }

        // ---- seasons ---------------------------------------------------------------------
        sectionHeading("Seasons")
        hint("A date range that replaces the normal week — summer hours, an off-season schedule. Both bounds are inclusive.")
        document.seasons.forEachIndexed { index, season ->
            seasonEditor(
                season,
                onChange = { document = document.copy(seasons = document.seasons.replacedAt(index, it)) },
                onRemove = { document = document.copy(seasons = document.seasons.withoutAt(index)) },
            )
        }
        addRowButton("Add a season") {
            document = document.copy(seasons = document.seasons + SeasonDoc())
        }

        // ---- notes and review ------------------------------------------------------------
        sectionHeading("Notes")
        sideBySideFields {
            dialogField("Review by", "When these hours should be re-checked.") {
                Input(InputType.Date) {
                    value(document.reviewBy.orEmpty())
                    onChange { document = document.copy(reviewBy = it.value.ifBlank { null }) }
                }
            }
            dialogField("") {}
        }
        dialogField("Hours notes", "Anything the structure cannot express — \"last boarding 30 minutes before close\".") {
            TextArea {
                style { height(60.px) }
                value(document.notes.orEmpty())
                onInput { document = document.copy(notes = it.value.ifBlank { null }) }
            }
        }

        // ---- what will be sent -----------------------------------------------------------
        Div({ style { marginTop(12.px) } }) {
            A(href = "javascript:void(0)", {
                onClick {
                    it.preventDefault()
                    showJson = !showJson
                }
            }) {
                Text(if (showJson) "Hide stored JSON" else "Show stored JSON")
            }
        }
        if (showJson) {
            Pre({
                style {
                    property("white-space", "pre-wrap")
                    property("word-break", "break-word")
                    backgroundColor(Theme.defaultDivider.value())
                    padding(8.px)
                    fontSize(0.8.cssRem)
                }
            }) {
                Text(if (document.isEmpty()) "(no hours recorded)" else document.encode())
            }
        }
    }
}

/**
 * A list of weekly rules. Shared by the top-level week and by each season, which carries the same
 * structure.
 */
@Composable
private fun weeklyRulesEditor(rules: List<WeeklyRuleDoc>, onChange: (List<WeeklyRuleDoc>) -> Unit) {
    rules.forEachIndexed { index, rule ->
        indentedRow {
            // Days
            Div({ style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); property("gap", "10px") } }) {
                weekdayOrder.forEach { day ->
                    val checked = rule.days.any { it.equals(day.name, ignoreCase = true) }
                    simpleCheckBox(day.shortLabel(), checked) { on ->
                        val days = if (on) {
                            if (checked) rule.days else rule.days + day.name
                        } else {
                            rule.days.filterNot { it.equals(day.name, ignoreCase = true) }
                        }
                        onChange(rules.replacedAt(index, rule.copy(days = days)))
                    }
                }

                Div({ style { property("margin-left", "auto") } }) {
                    button("Remove day group") {
                        onChange(rules.withoutAt(index))
                    }
                }
            }

            if (rule.days.isEmpty()) {
                problemList(listOf("This group names no days; the server will reject it."), warning = true)
            }

            rangesEditor(rule.ranges) { onChange(rules.replacedAt(index, rule.copy(ranges = it))) }
        }
    }

    addRowButton("Add a day group") {
        onChange(rules + WeeklyRuleDoc())
    }
}

/**
 * The rows where open and close times are actually entered.
 *
 * The operator types the two times they think in; [minutesOpen] turns them into the stored
 * duration, and the hint reads the result back so a past-midnight close is visibly intentional.
 */
@Composable
private fun rangesEditor(ranges: List<OpenRangeDoc>, onChange: (List<OpenRangeDoc>) -> Unit) {
    ranges.forEachIndexed { index, range ->
        val openMinutes = parseClock(range.open)
        val durationMinutes = parseIsoMinutes(range.duration)
        val allDay = durationMinutes == ALL_DAY_MINUTES

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "8px")
                property("flex-wrap", "wrap")
                marginTop(6.px)
            }
        }) {
            Span({ style { fontSize(0.85.cssRem) } }) { Text("Opens") }
            Input(InputType.Time) {
                value(range.open)
                onChange { event ->
                    val newOpen = event.value
                    val newOpenMinutes = parseClock(newOpen)
                    // Keep the close time the operator already chose fixed, and re-derive the
                    // duration against the new opening time.
                    val existingClose = if (openMinutes != null && durationMinutes != null) {
                        closingMinutes(openMinutes, durationMinutes)
                    } else null
                    val duration = when {
                        allDay -> formatIsoMinutes(ALL_DAY_MINUTES)
                        newOpenMinutes != null && existingClose != null ->
                            formatIsoMinutes(minutesOpen(newOpenMinutes, existingClose))
                        else -> range.duration
                    }
                    onChange(ranges.replacedAt(index, range.copy(open = newOpen, duration = duration)))
                }
            }

            Span({ style { fontSize(0.85.cssRem) } }) { Text("closes") }
            Input(InputType.Time) {
                if (allDay) disabled()
                value(
                    if (allDay) ""
                    else if (openMinutes != null && durationMinutes != null)
                        formatClock(closingMinutes(openMinutes, durationMinutes))
                    else ""
                )
                onChange { event ->
                    val closeMinutes = parseClock(event.value)
                    val duration = if (openMinutes != null && closeMinutes != null) {
                        formatIsoMinutes(minutesOpen(openMinutes, closeMinutes))
                    } else ""
                    onChange(ranges.replacedAt(index, range.copy(duration = duration)))
                }
            }

            simpleCheckBox("Open 24 hours", allDay) { on ->
                val duration = if (on) formatIsoMinutes(ALL_DAY_MINUTES) else ""
                onChange(ranges.replacedAt(index, range.copy(duration = duration)))
            }

            icon("delete") {
                action { onChange(ranges.withoutAt(index)) }
            }

            rangeHint(openMinutes, durationMinutes)
        }
    }

    addRowButton("Add a time range") {
        onChange(ranges + OpenRangeDoc())
    }
}

/** Reads an entered range back in words, so a past-midnight close is obviously deliberate. */
@Composable
private fun rangeHint(openMinutes: Int?, durationMinutes: Int?) {
    Span({
        style {
            fontSize(0.8.cssRem)
            opacity(.7)
        }
    }) {
        when {
            openMinutes == null -> Text("— set an opening time")
            durationMinutes == null -> Text("— set a closing time")
            durationMinutes >= ALL_DAY_MINUTES -> Text("— open 24 hours")
            else -> {
                val close = closingMinutes(openMinutes, durationMinutes)
                val hours = durationMinutes / 60
                val minutes = durationMinutes % 60
                val length = buildString {
                    if (hours > 0) append("${hours}h")
                    if (minutes > 0) {
                        if (hours > 0) append(" ")
                        append("${minutes}m")
                    }
                }
                val wraps = close <= openMinutes
                Text(
                    "— ${clockLabel(openMinutes)} to ${clockLabel(close)}" +
                            (if (wraps) " the next day" else "") + " ($length)"
                )
            }
        }
    }
}

@Composable
private fun overrideEditor(
    override: DateOverrideDoc,
    onChange: (DateOverrideDoc) -> Unit,
    onRemove: () -> Unit,
) {
    indentedRow {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "10px")
                property("flex-wrap", "wrap")
            }
        }) {
            Span({ style { fontSize(0.85.cssRem) } }) { Text("Date") }
            Input(InputType.Date) {
                value(override.date)
                onChange { onChange(override.copy(date = it.value)) }
            }

            simpleCheckBox("Closed all day", override.closed) {
                onChange(override.copy(closed = it, ranges = if (it) emptyList() else override.ranges))
            }

            Div({ style { property("flex", "1"); property("min-width", "180px") } }) {
                simpleTextField(override.note) { onChange(override.copy(note = it)) }
            }

            icon("delete") { action { onRemove() } }
        }

        if (override.date.isBlank()) {
            problemList(listOf("This override has no date."), warning = true)
        }

        if (!override.closed) {
            rangesEditor(override.ranges) { onChange(override.copy(ranges = it)) }
        }
    }
}

@Composable
private fun seasonEditor(
    season: SeasonDoc,
    onChange: (SeasonDoc) -> Unit,
    onRemove: () -> Unit,
) {
    indentedRow {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "10px")
                property("flex-wrap", "wrap")
            }
        }) {
            Div({ style { property("flex", "1"); property("min-width", "160px") } }) {
                simpleTextField(season.name) { onChange(season.copy(name = it)) }
            }

            Span({ style { fontSize(0.85.cssRem) } }) { Text("from") }
            Input(InputType.Date) {
                value(season.start)
                onChange { onChange(season.copy(start = it.value)) }
            }
            Span({ style { fontSize(0.85.cssRem) } }) { Text("to") }
            Input(InputType.Date) {
                value(season.end)
                onChange { onChange(season.copy(end = it.value)) }
            }

            icon("delete") { action { onRemove() } }
        }

        if (season.start.isNotBlank() && season.end.isNotBlank() && season.end < season.start) {
            problemList(listOf("This season ends before it starts."), warning = true)
        }

        weeklyRulesEditor(season.weekly) { onChange(season.copy(weekly = it)) }
    }
}

// ---------------------------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------------------------

@Composable
private fun sectionHeading(title: String) {
    Div({
        style {
            fontWeight("bold")
            marginTop(18.px)
            marginBottom(4.px)
            paddingTop(10.px)
            border(0.px, LineStyle.Solid, Theme.defaultDivider.value())
            borderWidth(1.px, 0.px, 0.px, 0.px)
        }
    }) {
        Text(title)
    }
}

@Composable
private fun hint(text: String) {
    Div({
        style {
            fontSize(0.8.cssRem)
            opacity(.7)
            marginBottom(6.px)
        }
    }) {
        Text(text)
    }
}

@Composable
private fun indentedRow(content: @Composable () -> Unit) {
    Div({
        style {
            border(0.px, LineStyle.Solid, Theme.defaultDivider.value())
            borderWidth(0.px, 0.px, 0.px, 3.px)
            padding(8.px, 0.px, 8.px, 12.px)
            marginBottom(10.px)
        }
    }) {
        content()
    }
}

@Composable
private fun addRowButton(title: String, action: suspend () -> Unit) {
    Div({ style { marginTop(4.px); marginBottom(8.px) } }) {
        button(title, false, false, action)
    }
}

/**
 * The server reports every structural problem with a set of hours at once rather than throwing on
 * the first, so they are rendered as a list — that is the whole reason it returns a list.
 */
@Composable
private fun problemList(problems: List<String>, warning: Boolean = false) {
    Div({
        style {
            backgroundColor(Theme.messageAlertBackground.value())
            border(1.px, LineStyle.Solid, Theme.messageAlertBorder.value())
            padding(8.px)
            marginBottom(10.px)
            fontSize(0.85.cssRem)
        }
    }) {
        Div({ style { fontWeight("bold") } }) {
            Text(if (warning) "Check this" else "The server rejected these hours")
        }
        Ul({ style { property("margin", "4px 0 0 0"); property("padding-left", "18px") } }) {
            problems.forEach { Li { Text(it) } }
        }
    }
}

/**
 * Pull the individual structural problems out of the server's error message. The resolver joins
 * them with "; " behind an "invalid hours:" prefix; anything that does not match that shape is
 * shown whole rather than mangled.
 */
internal fun hoursProblems(message: String?): List<String> {
    val text = message?.takeIf { it.isNotBlank() }?.trim()
        ?: return listOf("The hours could not be saved.")
    val marker = "invalid hours:"
    val index = text.indexOf(marker, ignoreCase = true)
    val body = if (index >= 0) text.substring(index + marker.length).trim() else text
    return body.split("; ").map { it.trim() }.filter { it.isNotEmpty() }.ifEmpty { listOf(text) }
}
